import type { AccessControlProvider } from "@refinedev/core";
import type { AdminRole, AdminIdentity } from "@/providers/types";
import { authProvider } from "@/providers/authProvider";

type ResourceActionMatrix = Record<string, Record<string, AdminRole[]>>;

const ALLOWED_ROLES: ResourceActionMatrix = {
  users: {
    list: ["support", "superadmin"],
    show: ["support", "moderator", "superadmin"],
    ban: ["superadmin"],
    unban: ["superadmin"],
    grant: ["support", "superadmin"],
    force_end: ["superadmin"],
    view_content: ["moderator", "superadmin"],
  },
  entitlement: {
    show: ["support", "superadmin"],
    list: ["support", "superadmin"],
  },
  operators: {
    list: ["superadmin"],
    create: ["superadmin"],
    edit: ["superadmin"],
    delete: ["superadmin"],
  },
  audit: {
    list: ["superadmin"],
    show: ["superadmin"],
  },
  usage: {
    // Cross-account spend roll-up is an analytics surface; per-account spend is
    // read from the user card support already opens.
    list: ["analyst", "superadmin"],
    show: ["support", "analyst", "superadmin"],
  },
  places: {
    list: ["moderator", "superadmin"],
    takedown: ["moderator", "superadmin"],
    restore: ["moderator", "superadmin"],
  },
  picks: {
    list: ["support", "moderator", "superadmin", "analyst"],
    show: ["support", "moderator", "superadmin", "analyst"],
    edit: ["moderator", "superadmin"],
  },
  "picks-partners": {
    list: ["support", "superadmin", "analyst"],
    show: ["support", "superadmin", "analyst"],
    edit: ["superadmin"],
  },
  "picks-categories": {
    list: ["support", "moderator", "superadmin", "analyst"],
    show: ["support", "moderator", "superadmin", "analyst"],
    edit: ["superadmin"],
  },
  guides: {
    list: ["support", "moderator", "superadmin", "analyst"],
    show: ["support", "moderator", "superadmin", "analyst"],
    create: ["moderator", "superadmin"],
    edit: ["moderator", "superadmin"],
  },
  localities: {
    // Same audience as places: curating imagery is content moderation, not account
    // administration, so it stops at moderator rather than superadmin.
    list: ["moderator", "superadmin"],
    curate: ["moderator", "superadmin"],
  },
};

export type CanAccessParams = {
  resource: string | undefined;
  action: string;
  role: AdminRole | undefined;
};

export type CanAccessResult =
  | { can: true }
  | { can: false; reason: string };

export function canAccess({ resource, action, role }: CanAccessParams): CanAccessResult {
  if (!role) {
    return { can: false, reason: "Не авторизован" };
  }

  if (!resource) {
    return { can: false, reason: "Ресурс не указан" };
  }

  const resourceMatrix = ALLOWED_ROLES[resource];
  if (!resourceMatrix) {
    return { can: false, reason: "Раздел недоступен для вашей роли" };
  }

  const allowedRoles = resourceMatrix[action];
  if (!allowedRoles) {
    return { can: false, reason: "Действие недоступно для вашей роли" };
  }

  if (allowedRoles.includes(role)) {
    return { can: true };
  }

  return {
    can: false,
    reason: "Раздел недоступен для вашей роли",
  };
}

export const accessControlProvider: AccessControlProvider = {
  can: async ({ resource, action }) => {
    const identity = (await authProvider.getIdentity?.()) as AdminIdentity | null;
    const role = identity?.role;
    return canAccess({ resource, action, role });
  },

  options: {
    buttons: {
      enableAccessControl: true,
      hideIfUnauthorized: true,
    },
  },
};
